/**
 * Collections API slice.
 * Leaf module (Perf 3.1.e Step 4). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';

export const collectionsApi = {
  create: async ({ title, icon, customInstructions, defaults, parentId }) => {
    try {
      const body = { title };
      if (icon) body.icon = icon;
      if (customInstructions) body.customInstructions = customInstructions;
      if (defaults !== undefined) body.defaults = defaults;
      if (parentId) body.parentId = parentId;
      
      const response = await fetchWithAuth('/json/collections', {
        method: 'POST',
        body,
      });

      if (response.error) {
        throw new Error(response.message);
      }

      return response.collection;
    } catch (error) {
      throw error;
    }
  },

  update: async (collectionId, { title, customInstructions, icon, memoryEnabled, memoryMode, defaults, parentId }) => {
    try {
      const body = {};
      if (title !== undefined) body.title = title;
      if (customInstructions !== undefined) body.customInstructions = customInstructions;
      if (icon !== undefined) body.icon = icon;
      if (memoryEnabled !== undefined) body.memoryEnabled = memoryEnabled;
      if (memoryMode !== undefined) body.memoryMode = memoryMode;
      if (defaults !== undefined) body.defaults = defaults;
      if (parentId !== undefined) body.parentId = parentId;

      const response = await fetchWithAuth(`/json/collections/${collectionId}`, {
        method: 'PUT',
        body,
      });

      if (response.error) {
        throw new Error(response.message);
      }

      return response.collection;
    } catch (error) {
      throw error;
    }
  },

  bulkAddConversations: async (collectionId, conversationIds) => {
    try {
      const data = await fetchWithAuth(`/json/collections/${collectionId}/conversations/bulk-add`, {
        method: 'POST',
        body: { conversationIds }
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to bulk add conversations to collection', 400, data);
      }

      return data.collection;
    } catch (error) {
      throw error;
    }
  },

  addConversation: async (collectionId, conversationId) => {
    try {
      const response = await fetchWithAuth(`/json/collections/${collectionId}/conversations/${conversationId}`, {
        method: 'POST'
      });

      if (response.error) {
        throw new Error(response.message);
      }

      return response.collection;
    } catch (error) {
      throw error;
    }
  },

  removeConversation: async (collectionId, conversationId) => {
    try {
      const response = await fetchWithAuth(`/json/collections/${collectionId}/conversations/${conversationId}`, {
        method: 'DELETE'
      });

      if (response.error) {
        throw new Error(response.message);
      }

      return response.collection;
    } catch (error) {
      throw error;
    }
  },

  addFiles: async (collectionId, fileIds) => {
    try {
      const response = await fetchWithAuth(`/json/collections/${collectionId}/files`, {
        method: 'POST',
        body: {
          fileIds
        }
      });

      if (response.error) {
        throw new Error(response.message);
      }

      return response.collection;
    } catch (error) {
      throw error;
    }
  },

  removeFile: async (collectionId, fileId) => {
    try {
      const response = await fetchWithAuth(`/json/collections/${collectionId}/files/${fileId}`, {
        method: 'DELETE'
      });

      if (response.error) {
        throw new Error(response.message);
      }

      return response.collection;
    } catch (error) {
      throw error;
    }
  },

  delete: async (collectionId) => {
    try {
      const response = await fetchWithAuth(`/json/collections/${collectionId}/delete`, {
        method: 'DELETE'
      });

      if (response.error) {
        throw new Error(response.message);
      }

      return response;
    } catch (error) {
      throw error;
    }
  },

  fetch: async (page = 1) => {
    try {
      const response = await fetchWithAuth(`/json/collections?page=${page}`);

      if (response.error) {
        throw new Error(response.message);
      }

      return {
        collections: response.collections,
        pagination: response.pagination
      };
    } catch (error) {
      throw error;
    }
  },

  getById: async (collectionId) => {
    try {
      const response = await fetchWithAuth(`/json/collections/${collectionId}`);

      if (response.error) {
        throw new Error(response.message);
      }

      return response.collection;
    } catch (error) {
      throw error;
    }
  }
};
